import { useEffect, useMemo, useState } from 'react';
import Dropzone from 'react-dropzone';
import { noop } from 'lodash';

import { IDocumentsGetResponse } from '@/shared/endpoints/documents/models/response';
import { SxProps, Theme, useTheme } from '@mui/material';
import { Alert, Box, Button } from '@/shared/ui';
import DocumentImage from '../document-image';

import UploadImage from '@/assets/icons/upload-image.svg';
import UploadImageSimple from '@/assets/icons/upload.svg';

import { checkFileAccept } from '@/shared/helpers/input';
import { toast } from 'react-toastify';
import { checkIsImageBroken } from '@/shared/helpers/image';

interface IProps {
    invalid?: boolean;
    document?: IDocumentsGetResponse; // Hopefully this will cover our needs for upload
    file?: File;
    onUpload?(file?: File): Promise<void>;
    boxSx?: SxProps<Theme>;
    className?: string;
    style?: 'simple' | 'base';
    placeholder?: string;
    disabled?: boolean;
    testId?: string;
}

const inputAccept = '.jpg, .jpeg, .png';

export const DocumentUpload = ({
    testId,
    boxSx,
    invalid,
    file,
    onUpload,
    document,
    style = 'base',
    placeholder,
    disabled: _disabled
}: IProps) => {
    const theme = useTheme();
    const localFilePath = useMemo(() => (file ? URL.createObjectURL(file) : ''), [file]);
    const [loading, setLoading] = useState(false);
    const disabled = _disabled || loading;
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        return () => {
            localFilePath && URL.revokeObjectURL(localFilePath);
        };
    }, [localFilePath]);

    const invalidSx = invalid ? { border: '2px solid', borderColor: 'error.main' } : {};

    const handleCheckFile = async (file: File) => {
        const notAccepted = !checkFileAccept(inputAccept, file);
        const invalid = notAccepted || (await checkIsImageBroken(file));

        setErrorMessage(invalid ? `Invalid file type. Accept formats ${inputAccept}` : '');

        return !invalid;
    };

    const handleUpload = async (files: File[]) => {
        setLoading(true);

        if (!files?.length || (await handleCheckFile(files[0]))) {
            await onUpload?.(files?.[0]);
        }

        setLoading(false);
    };

    if (localFilePath || document) {
        return (
            <div className="flex flex-col gap-y-[15px]">
                <Dropzone onDrop={handleUpload} disabled={disabled}>
                    {({ getInputProps, getRootProps }) => {
                        const rootProps = getRootProps();
                        return (
                            <Box
                                {...rootProps}
                                onClick={noop}
                                sx={{
                                    position: 'relative',
                                    overflow: 'hidden',
                                    height: 160,
                                    [theme.breakpoints.up('lg')]: { padding: 0 },
                                    [theme.breakpoints.down('lg')]: { padding: 0 },
                                    border: errorMessage ? `2px solid #FF0707` : 'none',
                                    ...invalidSx,
                                    ...boxSx
                                }}
                                fullWidth
                                shadow
                                data-testid={testId}
                            >
                                {document ? (
                                    <DocumentImage bg path={document.path} />
                                ) : (
                                    <div
                                        className="h-full w-full"
                                        style={{ background: `url('${localFilePath}') center/contain no-repeat` }}
                                    />
                                )}

                                <Button
                                    theme="secondary"
                                    variant="contained"
                                    sx={{ position: 'absolute', top: '0.625rem', background: 'rgba(0, 61, 51, 0.75)', right: '0.625rem' }}
                                    onClick={rootProps.onClick}
                                    disabled={disabled}
                                    loading={disabled}
                                    testId="button-document-upload-change"
                                >
                                    Change
                                    <input data-testid="document-upload-change-input" {...getInputProps()} />
                                </Button>
                            </Box>
                        );
                    }}
                </Dropzone>
                {errorMessage && <Alert label={errorMessage} />}
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-y-[15px]">
            <Dropzone onDrop={handleUpload} disabled={loading}>
                {({ getInputProps, getRootProps }) => {
                    return (
                        <Box
                            {...getRootProps()}
                            sx={{
                                ...invalidSx,
                                ...boxSx,
                                bgcolor: style === 'base' ? 'primary.100' : 'primary.50',
                                borderRadius: style === 'base' ? '12px' : '4px',
                                border: errorMessage ? `2px solid #FF0707` : 'none'
                            }}
                            fullWidth
                            shadow
                        >
                            <div
                                className={`flex flex-col items-center justify-center ${!localFilePath && !document ? 'cursor-pointer' : ''}`}
                            >
                                {!localFilePath && !document && (
                                    <>
                                        <img
                                            src={style === 'base' ? UploadImage : UploadImageSimple}
                                            alt="upload image"
                                            className="mb-[1.1875rem]"
                                        />
                                        <p className="text-center font-atkinsonHyperlegible font-normal">
                                            {placeholder || 'Click here to browse your images or you may drop files here.'}
                                        </p>
                                    </>
                                )}

                                <input data-testid="document-upload-input" {...getInputProps()} />
                            </div>
                        </Box>
                    );
                }}
            </Dropzone>
            {errorMessage && <Alert label={errorMessage} />}
        </div>
    );
};
